import { type CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignProductsByProjectCode, useApiMutationCampaignProductsByProjectCode } from '@hooks-api/fortune-fastpass/use-api-campaign-products-byprojectcode'
import { type ProductByProjectCode } from '@hooks-api/types/apiRequest'
import { type Nullable } from '@hooks-api/types/nullable'
import { safeJsonParse } from '@utils/safeJsonParse'
import { safeJsonStringify } from '@utils/safeJsonStringify'
import { useMemo } from 'react'
import { parseProductMap } from '../parser/parseProductMap'
import { type CartItem } from '../types'

const parseCartItemProductIdsSet = (cartItems: CartItem[], extraProductIds?: ProductByProjectCode[]) => {
  const productIdsSet = new Set<string>(extraProductIds?.map(item => safeJsonStringify(item, '')))
  for (const item of cartItems) {
    productIdsSet.add(safeJsonStringify({ product_id: item.cartItemId, project_code: item.project_code }, ''))
  }
  return Array.from(productIdsSet).map(item => safeJsonParse<ProductByProjectCode>(item)).filter(item => item !== undefined)
}

export interface UseCampaignProductsInfoByProjectCodeProps {
  cartItems: CartItem[]
  countryCode: CountryCode
  campaignCode: Nullable<string>
  merchantId?: Nullable<number>
  extraProductIds?: ProductByProjectCode[]
}

export const useCampaignProductsInfoByProjectCode = (props: UseCampaignProductsInfoByProjectCodeProps) => {
  const { cartItems, countryCode, campaignCode, merchantId, extraProductIds } = props
  const { trigger: getProducts } = useApiMutationCampaignProductsByProjectCode()
  const productsSet = useMemo(() => parseCartItemProductIdsSet(cartItems, extraProductIds), [cartItems, extraProductIds])
  const { destructuredData: productsData, mutate, isLoading: isProductsLoading } = useApiCampaignProductsByProjectCode({
    country_code: countryCode,
    products: [...productsSet],
    campaign_code: campaignCode,
    merchant_id: merchantId,
  }, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  })
  const productMap = useMemo(() => parseProductMap(productsData), [productsData])

  const revalidateProductsInfo = useCallbackEvent(async (extraProductIds?: ProductByProjectCode[]) => {
    const productsSet = parseCartItemProductIdsSet(cartItems, extraProductIds)
    const res = await getProducts({
      country_code: countryCode,
      campaign_code: campaignCode,
      merchant_id: merchantId,
      products: [...productsSet],
    })
    const newProducts = res.data.data
    let nextProductMap = { ...productMap }
    newProducts.forEach((product) => {
      nextProductMap = {
        ...nextProductMap,
        [product.id]: product,
      }
    })

    mutate({
      ...res,
      data: {
        ...res.data,
        data: Object.values(nextProductMap).filter(product => product !== undefined),
      },
    }, {
      revalidate: false,
    })
    return res.data.data
  })

  const getProductsInfo = useCallbackEvent(async (extraProductIds?: ProductByProjectCode[]) => {
    const productsSet = parseCartItemProductIdsSet(cartItems, extraProductIds)
    const { data } = await getProducts({
      country_code: countryCode,
      campaign_code: campaignCode,
      merchant_id: merchantId,
      products: [...productsSet],
    })
    return data.data
  })

  return {
    productMap,
    revalidateProductsInfo,
    getProductsInfo,
    isProductsLoading,
  }
}
